import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// User's news subscriptions. Backed by news_user_subscriptions table.
// Each row points to ONE of: source_id (subscribe to a feed) or tag
// (subscribe to a topic keyword). notify_mode: off / instant / daily.

export function useNewsSubscriptions() {
  const { user } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const initialRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialRef.current) setLoading(true)
    const { data } = await supabase
      .from('news_user_subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
    setRows(data || [])
    initialRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  // Lookup helpers for the news UI.
  const bySource = useMemo(() => {
    const m = new Map()
    for (const r of rows) if (r.source_id) m.set(r.source_id, r)
    return m
  }, [rows])

  const byTag = useMemo(() => {
    const m = new Map()
    for (const r of rows) if (r.tag) m.set(r.tag.toLowerCase(), r)
    return m
  }, [rows])

  // Subscribe / update — pass exactly one of source_id or tag.
  const subscribe = useCallback(async ({ source_id, tag, notify_mode = 'daily' }) => {
    if (!user) return null
    const payload = {
      user_id: user.id,
      source_id: source_id || null,
      tag: tag ? tag.trim().toLowerCase() : null,
      notify_mode,
    }
    if (!payload.source_id && !payload.tag) return null
    // Use upsert with the unique partial indexes — but Postgres
    // doesn't honour partial indexes for ON CONFLICT, so we have
    // to UPDATE-OR-INSERT manually.
    const existing = source_id
      ? bySource.get(source_id)
      : tag ? byTag.get(tag.toLowerCase()) : null
    if (existing) {
      const { error } = await supabase
        .from('news_user_subscriptions')
        .update({ notify_mode })
        .eq('id', existing.id)
      if (error) return null
    } else {
      const { error } = await supabase
        .from('news_user_subscriptions')
        .insert(payload)
      if (error) return null
    }
    await fetch()
    return true
  }, [user, bySource, byTag, fetch])

  const unsubscribe = useCallback(async ({ source_id, tag }) => {
    if (!user) return false
    const existing = source_id
      ? bySource.get(source_id)
      : tag ? byTag.get(tag.toLowerCase()) : null
    if (!existing) return false
    const { error } = await supabase
      .from('news_user_subscriptions')
      .delete()
      .eq('id', existing.id)
    if (error) return false
    await fetch()
    return true
  }, [user, bySource, byTag, fetch])

  return {
    rows,
    bySource,
    byTag,
    loading,
    subscribe,
    unsubscribe,
    refetch: fetch,
  }
}

// Articles matching the user's subscriptions, regardless of age.
// Powers the "From my subs" in-app view. Defaults to unread only.
export function useSubscriptionArticles({ unreadOnly = true, limit = 50 } = {}) {
  const { user } = useAuth()
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase.rpc('list_subscription_articles', {
      p_limit: limit,
      p_unread_only: unreadOnly,
    })
    if (err) setError(err.message)
    else setArticles(data || [])
    initialRef.current = true
    setLoading(false)
  }, [user, unreadOnly, limit])

  useEffect(() => { fetch() }, [fetch])

  return { articles, loading, error, refetch: fetch }
}

// Count of unread subscription matches — for the bell icon badge.
export function useSubscriptionUnreadCount() {
  const { user } = useAuth()
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    const { data } = await supabase.rpc('count_subscription_unread')
    setCount(typeof data === 'number' ? data : 0)
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  return { count, loading, refetch: fetch }
}
